// ABOUTME: Horizontal scroll container with fade gradient hints on edges
// ABOUTME: Shows visual indicator when content is scrollable (left/right gradients appear based on scroll position)

import type { FC, ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@shared/lib/utils"

interface CHorizontalScrollProps {
  children: ReactNode
  className?: string
  fadeClassName?: string
  showFadeOnDesktop?: boolean
}

export const CHorizontalScroll: FC<CHorizontalScrollProps> = ({
  children,
  className,
  fadeClassName,
  showFadeOnDesktop = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [showRightFade, setShowRightFade] = useState(false)

  const updateFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el
    const hasOverflow = scrollWidth > clientWidth

    setShowLeftFade(hasOverflow && scrollLeft > 0)
    setShowRightFade(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateFades()

    el.addEventListener("scroll", updateFades)
    window.addEventListener("resize", updateFades)

    return () => {
      el.removeEventListener("scroll", updateFades)
      window.removeEventListener("resize", updateFades)
    }
  }, [updateFades])

  // Update fades when children change
  useEffect(() => {
    updateFades()
  }, [children, updateFades])

  const fadeVisibility = showFadeOnDesktop ? "" : "md:hidden"

  return (
    <div className={cn("relative", className)}>
      <div
        ref={scrollRef}
        className="overflow-x-auto scroll-hide"
      >
        {children}
      </div>
      {/* Left fade gradient */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none transition-opacity duration-200",
          fadeVisibility,
          fadeClassName,
          showLeftFade ? "opacity-100" : "opacity-0",
        )}
      />
      {/* Right fade gradient */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none transition-opacity duration-200",
          fadeVisibility,
          fadeClassName,
          showRightFade ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  )
}
